import * as userAction from "../actions/userActions";
import * as meActions from "../actions/meActions";
import { post, get } from "./axiosClient";
import * as requestActions from "../actions/requestActions";
import * as settingsActions from "../actions/settingsActions";
import { resolveLanguage } from "../utils/language";
import {
  canBypassMaintenance,
  isMaintenanceModeEnabled,
} from "../utils/maintenanceAccess";
import sha512 from "js-sha512";
import isElectron from "is-electron";

let store;
let roomClient;
let messagingClient;
let intl;
let desktopSsoAttempted = false;

function normalizeOsUsername(raw) {
  if (raw == null) return "";
  let s = String(raw).trim();
  if (!s) return "";
  const slash = Math.max(s.lastIndexOf("\\"), s.lastIndexOf("/"));
  if (slash >= 0) s = s.slice(slash + 1);
  const at = s.indexOf("@");
  if (at > 0) s = s.slice(0, at);
  return s.trim();
}

function applySuccessfulLogin(user, token, roomId) {
  if (!roomId) store.dispatch(meActions.setUIStep("MANAGEMENT_SCREEN"));
  localStorage.removeItem("token");
  localStorage.setItem("token", token);
  try {
    localStorage.setItem("user", JSON.stringify(user));
  } catch (e) {
    /* ignore */
  }
  roomClient.setPeerId(user.id);
  store.dispatch(userAction.setUser(user));
  store.dispatch(
    settingsActions.setDisplayName(
      `${user.firstName || ""} ${user.lastName || ""}`.trim()
    )
  );
  store.dispatch(meActions.addRole(user.meetingRole));
  store.dispatch(meActions.loggedIn(true));
  try {
    const connectStatus = ["online", "busy", "away"].includes(
      user.presenceStatus
    )
      ? user.presenceStatus
      : "online";
    messagingClient.connect(connectStatus);
  } catch (e) {
    /* messaging optional */
  }
  store.dispatch(
    requestActions.notify({
      text: intl.formatMessage({
        id: "room.loggedIn",
        defaultMessage: "You are logged in",
      }),
    })
  );
}

export default class AuthService {
  static init(storeObj, messagingClientObj, roomClientObj, intlObj) {
    store = storeObj;
    roomClient = roomClientObj;
    messagingClient = messagingClientObj;
    intl = intlObj;
  }

  login(username, password, roomId) {
    store.dispatch(userAction.setLoginProgress(true));
    const language = resolveLanguage(store.getState().user.userLanguage);
    // password = plaintext; hash for local users, plainPassword for LDAP bind
    post(`/auth/`, {
      username: username,
      password: sha512(password),
      plainPassword: password,
      lang: language,
    })
      .then((response) => {
        if (response.status === 200) {
          if (response.data.error === "INCORRECT_USERNAME_OR_PASSWORD") {
            store.dispatch(
              requestActions.notify({
                type: "error",
                text: intl.formatMessage({
                  id: "user.wrongUsernameOrPassword",
                  defaultMessage: "Wrong username or password",
                }),
              })
            );
          } else if (
            response.data.error === "FAILED" &&
            response.data.payload === "TOO_MANY_PASSWORD_ATTEMPTS"
          ) {
            store.dispatch(
              requestActions.notify({
                type: "error",
                text: intl.formatMessage({
                  id: "user.tooManyPasswordAttempts",
                  defaultMessage:
                    "Too many password attempts, please wait for a while",
                }),
              })
            );
          } else if (
            response.data.error === "FAILED" &&
            response.data.payload === "USER_DELETED"
          ) {
            store.dispatch(
              requestActions.notify({
                type: "error",
                text: intl.formatMessage({
                  id: "user.userDeleted",
                  defaultMessage: "This user is deleted.",
                }),
              })
            );
          } else if (
            response.data.error === "FAILED" &&
            response.data.payload === "MAINTENANCE_MODE"
          ) {
            store.dispatch(
              requestActions.notify({
                type: "error",
                text: intl.formatMessage({
                  id: "user.maintenanceMode",
                  defaultMessage:
                    "Sistem bakımdadır. Yalnızca root admin giriş yapabilir.",
                }),
              })
            );
          } else if (response.data.payload && response.data.payload.token) {
            const user = response.data.payload.user;
            if (
              isMaintenanceModeEnabled() &&
              !canBypassMaintenance(user)
            ) {
              store.dispatch(
                requestActions.notify({
                  type: "error",
                  text: intl.formatMessage({
                    id: "user.maintenanceMode",
                    defaultMessage:
                      "Sistem bakımdadır. Yalnızca root admin giriş yapabilir.",
                  }),
                })
              );
              store.dispatch(userAction.setLoginProgress(false));
              return;
            }
            applySuccessfulLogin(user, response.data.payload.token, roomId);
          }
        }
        store.dispatch(userAction.setLoginProgress(false));
      })
      .catch(() => {
        store.dispatch(meActions.loggedIn(false));
        store.dispatch(userAction.setLoginProgress(false));
      });
  }

  /**
   * Electron only: OS Windows kullanıcı adı → LDAP User.username eşleşirse şifresiz giriş.
   * Başarısız olursa sessizce login ekranına düşer (username prefilleri localStorage'a yazılır).
   */
  tryDesktopAutoLogin(roomId) {
    if (desktopSsoAttempted) return Promise.resolve(false);
    desktopSsoAttempted = true;

    const cfg = typeof window !== "undefined" ? window.config : null;
    const desktop =
      typeof window !== "undefined" ? window.wunityDesktop : null;
    if (
      !isElectron() ||
      !cfg ||
      !cfg.desktopSsoEnabled ||
      !desktop ||
      typeof desktop.getOsUsername !== "function"
    ) {
      return Promise.resolve(false);
    }

    const secret = String(cfg.desktopSsoSecret || "").trim();
    if (!secret) return Promise.resolve(false);

    store.dispatch(userAction.setLoginProgress(true));
    const language = resolveLanguage(store.getState().user.userLanguage);

    return desktop
      .getOsUsername()
      .then((info) => {
        const username = normalizeOsUsername(
          (info && info.username) || ""
        );
        if (!username) {
          store.dispatch(userAction.setLoginProgress(false));
          return false;
        }
        try {
          localStorage.setItem("desktopSsoUsernameHint", username);
        } catch (e) {
          /* ignore */
        }
        return post(`/auth/desktop-sso`, {
          username,
          secret,
          lang: language,
        }).then((response) => {
          if (
            response.status === 200 &&
            response.data &&
            response.data.payload &&
            response.data.payload.token
          ) {
            const user = response.data.payload.user;
            if (
              isMaintenanceModeEnabled() &&
              !canBypassMaintenance(user)
            ) {
              store.dispatch(userAction.setLoginProgress(false));
              return false;
            }
            applySuccessfulLogin(
              user,
              response.data.payload.token,
              roomId
            );
            store.dispatch(userAction.setLoginProgress(false));
            return true;
          }
          store.dispatch(userAction.setLoginProgress(false));
          return false;
        });
      })
      .catch(() => {
        store.dispatch(meActions.loggedIn(false));
        store.dispatch(userAction.setLoginProgress(false));
        return false;
      });
  }

  checkAuth(redirectToRoom) {
    store.dispatch(userAction.setLoginProgress(true));
    const existingToken = localStorage.getItem("token");
    const goLogin = () => {
      store.dispatch(meActions.loggedIn(false));
      if (!redirectToRoom) {
        store.dispatch(meActions.setUIStep("AUTHENTICATED_ENTRANCE_SCREEN"));
      }
      // Geçersiz / eksik oturumda Electron LDAP auto-login dene
      this.tryDesktopAutoLogin(redirectToRoom);
    };
    const restoreCachedUser = () => {
      try {
        const cached = localStorage.getItem("user");
        if (!cached || !cached.startsWith("{")) return null;
        const user = JSON.parse(cached);
        if (!user || !user.id) return null;
        store.dispatch(userAction.setUser(user));
        store.dispatch(
          settingsActions.setDisplayName(
            `${user.firstName || ""} ${user.lastName || ""}`.trim()
          )
        );
        if (user.meetingRole) {
          store.dispatch(meActions.addRole(user.meetingRole));
        }
        try {
          roomClient.setPeerId(user.id);
        } catch (e) {
          /* ignore */
        }
        return user;
      } catch (e) {
        return null;
      }
    };

    get(`/auth/check`)
      .then((response) => {
        if (response.status === 200 && response.data && response.data.payload) {
          if (!redirectToRoom) {
            store.dispatch(meActions.setUIStep("MANAGEMENT_SCREEN"));
          }
          roomClient.setPeerId(response.data.payload.id);
          store.dispatch(meActions.loggedIn(true));
          try {
            localStorage.setItem(
              "user",
              JSON.stringify(response.data.payload)
            );
          } catch (e) {
            /* ignore */
          }
          store.dispatch(userAction.setUser(response.data.payload));
          store.dispatch(meActions.addRole(response.data.payload.meetingRole));
          try {
            const ps = response.data.payload.presenceStatus;
            const connectStatus = ["online", "busy", "away"].includes(ps)
              ? ps
              : "online";
            messagingClient.connect(connectStatus);
          } catch (e) {
            /* messaging optional for portal shell */
          }
          store.dispatch(
            settingsActions.setDisplayName(
              response.data.payload.firstName +
                " " +
                response.data.payload.lastName
            )
          );
        } else if (response.status === 200 && existingToken) {
          // Token var ama payload yoksa cache ile devam et; yoksa login
          const user = restoreCachedUser();
          if (user) {
            store.dispatch(meActions.loggedIn(true));
            if (!redirectToRoom) {
              store.dispatch(meActions.setUIStep("MANAGEMENT_SCREEN"));
            }
            try {
              const connectStatus = ["online", "busy", "away"].includes(
                user.presenceStatus
              )
                ? user.presenceStatus
                : "online";
              messagingClient.connect(connectStatus);
            } catch (e) {
              /* ignore */
            }
          } else {
            localStorage.removeItem("token");
            goLogin();
          }
        } else {
          goLogin();
        }
        store.dispatch(userAction.setLoginProgress(false));
      })
      .catch((r) => {
        const status = r && r.response && r.response.status;
        // 401/403: oturum geçersiz → login
        if (status === 401 || status === 403) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          goLogin();
          store.dispatch(userAction.setLoginProgress(false));
          return;
        }
        // Ağ/TLS hatası: yalnızca geçerli cache user varsa oturumu koru
        if (existingToken && (!status || status >= 500)) {
          const user = restoreCachedUser();
          if (user) {
            store.dispatch(meActions.loggedIn(true));
            if (!redirectToRoom) {
              store.dispatch(meActions.setUIStep("MANAGEMENT_SCREEN"));
            }
            try {
              const connectStatus = ["online", "busy", "away"].includes(
                user.presenceStatus
              )
                ? user.presenceStatus
                : "online";
              messagingClient.connect(connectStatus);
            } catch (e) {
              /* ignore */
            }
          } else {
            localStorage.removeItem("token");
            goLogin();
          }
        } else {
          goLogin();
        }
        store.dispatch(userAction.setLoginProgress(false));
      });
  }
}
